import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import UserSettingsAndAccessForm from '../UserSettingsAndAccessForm';
import { getUserPermissions } from './UserSettings';
import { useAdminFeatureFlag } from 'admin/hooks/useAdminFeatureFlag';
import {
  UserSettings,
  UserOrganizationSettings,
  AdminStateSlice,
  PermissionMap,
  UserData,
} from 'admin/types';
import TCClient from 'client';

type UserSettingsFormProps = {
  selectedOrganization: AdminStateSlice['selectedOrganization'];
  selectedUser: AdminStateSlice['selectedUser'];
  userSettings?: UserSettings;
  setUserSettings: React.Dispatch<React.SetStateAction<UserSettings | undefined>>;
  setUserSettingsCopy: React.Dispatch<React.SetStateAction<UserSettings | undefined>>;
  userOrgSettings?: UserOrganizationSettings;
  setUserOrgSettings: (os: UserOrganizationSettings) => void;
  featureFlags: Record<string, unknown>;
  urlParams: Record<string, string>;
  userPermissions: PermissionMap | undefined;
  setUserPermissions: React.Dispatch<React.SetStateAction<PermissionMap | undefined>>;
  setUserPermissionsCopy: React.Dispatch<React.SetStateAction<PermissionMap | undefined>>;
  userData?: UserData;
  // Increments after each successful save, so the form can forget values it remembered before.
  savedVersion?: number;
};
export default function UserSettingsForm({
  selectedOrganization,
  selectedUser,
  userSettings,
  setUserSettings,
  setUserSettingsCopy,
  userOrgSettings,
  setUserOrgSettings,
  userPermissions,
  setUserPermissions,
  setUserPermissionsCopy,
  userData,
  savedVersion,
}: UserSettingsFormProps) {
  const { ALLOW_CLINICAL_WORKFLOWS } = useAdminFeatureFlag(['ALLOW_CLINICAL_WORKFLOWS']);

  useEffect(() => {
    async function getOrganizationSettings() {
      const org_settings = await TCClient.adminUsers.fetchOrganizationSettings({
        organizationId: selectedOrganization.token,
      });
      setUserOrgSettings(org_settings);

      const user_settings = (await TCClient.adminUsers.getUserSettings({
        userId: selectedUser.id,
        organizationId: selectedOrganization.token,
      })) as UserSettings;
      setUserSettings(user_settings);
      setUserSettingsCopy(user_settings);

      const user_permissions = await getUserPermissions(
        selectedOrganization.token,
        selectedUser.id,
        undefined,
        ALLOW_CLINICAL_WORKFLOWS
      );
      setUserPermissions(user_permissions);
      setUserPermissionsCopy(user_permissions);
    }
    try {
      getOrganizationSettings();
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [
    selectedOrganization.token,
    selectedUser.id,
    setUserOrgSettings,
    setUserPermissions,
    setUserPermissionsCopy,
    setUserSettings,
    setUserSettingsCopy,
    ALLOW_CLINICAL_WORKFLOWS,
  ]);

  if (!userOrgSettings || !userSettings) {
    return null;
  }

  return (
    <UserSettingsAndAccessForm
      userData={userData}
      userPermissions={userPermissions}
      setUserPermissions={setUserPermissions}
      setUserSettings={setUserSettings}
      userSettings={userSettings}
      savedVersion={savedVersion}
    />
  );
}
